import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from "react-router-dom";
import toast from 'react-hot-toast';
import api from 'state/axios';
import { useTheme, useMediaQuery, Box,
  CircularProgress, Typography, Tooltip, IconButton,
} from '@mui/material';
import {
  AddCircleOutline,
  Edit,
  Delete,
  DeleteForever,
} from '@mui/icons-material';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import { hasPermission } from 'state/checkPermission';
import { getSelectedIds, isAnyRowSelected } from 'state/helpers';
import DataGridComponent from 'components/DataGridComponent';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import Header from 'components/Header';
import StyledButton from 'components/StyledButton';
import DeleteConfirm from 'components/DeleteConfirm';
import CardList from 'components/CardList';


const Suppliers = () => {
  useAuthCheck();
  const navigate = useNavigate();
  const user = useSelector((state) => state.user);
  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

  const canCreate = hasPermission(user?.permissions, "Suppliers", "create", false);
  const canEdit   = hasPermission(user?.permissions, "Suppliers", "update", false);
  const canDelete = hasPermission(user?.permissions, "Suppliers", "delete", false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [rowSelectionModel, setRowSelectionModel] = useState({
    type: 'include',
    ids: new Set(),
  });

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [deleteType, setDeleteType] = useState(null);
  const [idToDelete, setIdToDelete] = useState(null);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({ _id: false, city: false, country: false });


  useEffect(() => {
    const handleSuppliers = async () => {
      try {
        setError(null);
        setLoading(true);
        const res = await api.get('/suppliers');
        setSuppliers(res.data);
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    };

    handleSuppliers();
  }, []);

  const handleDeleteClick = (id) => {
    setIdToDelete(id);
    setDeleteType('single');
    setConfirmDialogOpen(true);
  };

  const handleBulkDelete = () => {
    const selectedIds = getSelectedIds(rowSelectionModel, suppliers);
    if (selectedIds.length === 0) {
      toast.error('No rows selected for deletion!');
      return;
    }
    setDeleteType('bulk');
    setConfirmDialogOpen(true);
  };

  const executeDelete = useCallback(async () => {
    try {
      if (!canDelete) return;

      if (deleteType === 'single') {
        if (!idToDelete) return;
        await api.delete(`/suppliers/delete/${idToDelete}`);
        setSuppliers(suppliers.filter((item) => item._id !== idToDelete));
        toast.success('Supplier deleted successfully.');
      } else if (deleteType === 'bulk') {
        const selectedIds = getSelectedIds(rowSelectionModel, suppliers);
        if (selectedIds.length === 0) {
          toast.error('No rows selected for deletion!');
          return;
        }
        const res = await api.patch('/suppliers/soft-delete', { ids: selectedIds });
        toast.success(res.data?.message || `${selectedIds.length} record(s) deleted successfully.`);
        setSuppliers(suppliers.filter((item) => !selectedIds.includes(item._id)));
      }
    } catch (err) {
      toast.error(err.message || "Failed to delete suppliers!");
    }
  }, [canDelete, deleteType, idToDelete, rowSelectionModel, suppliers]);

  const columns = useMemo(() => [
    {
      field: "no",
      headerName: "No",
      filterable: false,
      sortable: false,
      renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,
      ...(isMobile ? { width: 60 } : { flex: 0.8 }),
    },
    {
      field: "supplierCode",
      headerName: "Code",
      ...(isMobile ? { width: 80 } : { flex: 1 }),
    },
    {
      field: "supplierName",
      headerName: "Supplier Name",
      ...(isMobile ? { width: 250 } : { flex: 3 }),
    },
    {
      field: "contactPerson",
      headerName: "ContactPerson",
      ...(isMobile ? { width: 250 } : { flex: 2 }),
    },
    {
      field: "email",
      headerName: "Email",
      ...(isMobile ? { width: 120 } : { flex: 1.5 }),
    },
    {
      field: "phone",
      headerName: "Phone",
      ...(isMobile ? { width: 100 } : { flex: 1.2 }),
    },
    {
      field: "address",
      headerName: "Address",
      ...(isMobile ? { width: 120 } : { flex: 1.5 }),
    },
    {
      field: "city",
      headerName: "City",
      ...(isMobile ? { width: 100 } : { flex: 1.2 }),
    },
    {
      field: "country",
      headerName: "Country",
      ...(isMobile ? { width: 100 } : { flex: 1.2 }),
    },
    {
      field: "isActive",
      headerName: "IsActive",
      ...(isMobile ? { width: 100 } : { flex: 1.2 }),
    },
  ], [isMobile]);

  const actionColumn = useMemo(() => {
    const activeCount = [canEdit, canDelete].filter(Boolean).length;
    if (activeCount === 0) return [];

    const baseWidth = 60 + activeCount * 25;

    return [
      {
        field: "action",
        headerName: "Action",
        filterable: false,
        sortable: false,
        width: baseWidth,
        renderCell: (params) => (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            gap={1}
            sx={{ width: '100%', height: '100%' }}
          >
            {canEdit && (
              <Tooltip title="Edit">
                <IconButton
                  aria-label="Edit"
                  sx={{
                    color: theme.palette.primary.dark,
                    '&:hover': { color: theme.palette.green.deep },
                  }}
                  onClick={() => { if (!canEdit) return; navigate(`/suppliers/${params.row._id}`); }}
                >
                  <Edit />
                </IconButton>
              </Tooltip>
            )}
            {canDelete && (
              <Tooltip title="Delete">
                <IconButton
                  aria-label="Delete"
                  sx={{
                    color: theme.palette.primary.dark,
                    '&:hover': { color: theme.palette.green.deep },
                  }}
                  onClick={() => { if (!canDelete) return; handleDeleteClick(params.row._id); }}
                >
                  <DeleteForever />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        ),
      },
    ];
  }, [navigate, theme.palette, canEdit, canDelete]);

  const columnsToShow = [...columns, ...actionColumn];

  const gridStyles = {
    "& .MuiDataGrid-row.isActive-false": {
      backgroundColor: `${theme.palette.primary.light} !important`,
    },
  };


  return (
    <Box className="main-container-box">
      <FlexBetween>
        <Header
          title="Suppliers"
          subtitle={`${loading ? 'Loading...' : 'List of Suppliers'}`}
        />
        {!loading && (
          <Box display="flex" alignItems="center" gap={1}>
            {!isMobile && canDelete && (
              <Tooltip title="Bulk soft delete" arrow>
                <span>
                  <StyledButton
                    disabled={!isAnyRowSelected(rowSelectionModel, suppliers)}
                    onClick={handleBulkDelete}
                  >
                    <Delete />
                  </StyledButton>
                </span>
              </Tooltip>
            )}
            {canCreate && (
              <Tooltip title="Create" arrow>
                <span>
                  <StyledButton onClick={() => navigate("/suppliers/new")}>
                    <AddCircleOutline />
                  </StyledButton>
                </span>
              </Tooltip>
            )}
          </Box>
        )}
      </FlexBetween>
      {loading ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <CircularProgress />
        </FlexCenter>
      ) : error ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <Typography color="error">Error: {error?.message || ''}</Typography>
        </FlexCenter>
      ) : (
        <Box
          display="grid"
          gridTemplateColumns="repeat(12, minmax(0, 1fr))"
          gap={2}
          sx={{ "& > div": { gridColumn: isDesktop ? undefined : "span 12" } }}
        >
          <Box height={LAYOUT_HEIGHT} sx={{ gridColumn: "span 12" }}>
            {isMobile ? (
              <CardList
                rows={suppliers}
                primaryField="supplierName"
                fields={[
                  { label: "Code", key: "supplierCode" },
                  { label: "Contact", key: "contactPerson" },
                  { label: "Phone", key: "phone" },
                  { label: "Email", key: "email" },
                  { label: "Address", key: "address" },
                ]}
                canEdit={canEdit}
                canDelete={canDelete}
                onEdit={(id) => navigate(`/suppliers/${id}`)}
                onDelete={handleDeleteClick}
              />
            ) : (
              <DataGridComponent
                rows={suppliers || []}
                columns={columnsToShow}
                columnVisibilityModel={columnVisibilityModel}
                setColumnVisibilityModel={setColumnVisibilityModel}
                loading={loading}
                props={{
                  checkboxSelection: true,
                  disableRowSelectionOnClick: true,
                  onRowSelectionModelChange: setRowSelectionModel,
                  rowSelectionModel,
                  getRowClassName: (params) => params.row.isActive === false ? 'isActive-false' : '',
                  sx: gridStyles,
                  initialState: { pagination: { paginationModel: { pageSize: 100 } } },
                }}
              />
            )}
          </Box>
        </Box>
      )}

      <DeleteConfirm
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={executeDelete}
        deleteType={deleteType}
      />
    </Box>
  );
};

export default Suppliers;
